import { useState, useMemo } from 'react'
import {
  X,
  Users,
  Search,
  Shield,
  Check,
  XCircle,
  ArrowUpDown,
  Calendar,
  MessageSquare,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useUsers, useApproveUser, useRevokeUser } from '@/hooks'
import { cn } from '@/lib/utils'
import { getAvatarColor, getInitials } from '@/lib/utils/avatarUtils'

type SortOrder = 'newest' | 'oldest'
type StatusFilter = 'all' | 'pending' | 'approved'

interface UserManagementSheetProps {
  open: boolean
  onClose: () => void
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHrs = Math.floor(diffMins / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return formatDate(dateStr)
}

export function UserManagementSheet({
  open,
  onClose,
}: UserManagementSheetProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const { data: users = [], isLoading } = useUsers()
  const approveMutation = useApproveUser()
  const revokeMutation = useRevokeUser()

  const filteredAndSortedUsers = useMemo(() => {
    let result = users.filter((user) => {
      if (statusFilter === 'pending' && user.isApproved) return false
      if (statusFilter === 'approved' && !user.isApproved) return false

      if (!searchQuery) return true
      const q = searchQuery.toLowerCase()
      return (
        user.username.toLowerCase().includes(q) ||
        user.displayName?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q)
      )
    })

    result = [...result].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB
    })

    return result
  }, [users, searchQuery, sortOrder, statusFilter])

  const pendingCount = users.filter((u) => !u.isApproved).length
  const approvedCount = users.filter((u) => u.isApproved).length
  const smsCount = users.filter((u) => u.smsSubscribed).length

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-[98vh] max-h-[98vh] w-full sm:max-w-md p-0">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Users className="w-5 h-5 text-black" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold text-white">
                User Management
              </SheetTitle>
              <p className="text-xs text-neutral-400 mt-0.5">
                {users.length} users
                {pendingCount > 0 && ` · ${pendingCount} pending`}
                {smsCount > 0 && ` · ${smsCount} SMS`}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Search + Filters */}
        <div className="px-5 py-3 border-b border-white/[0.06] space-y-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="pl-9 bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 h-10"
            />
          </div>
          <div className="flex items-center gap-2">
            {/* Status filter pills */}
            {(['all', 'pending', 'approved'] as StatusFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                  statusFilter === f
                    ? 'bg-white/10 text-white'
                    : 'text-neutral-500 hover:text-neutral-300 hover:bg-white/[0.04]'
                )}
              >
                {f === 'all'
                  ? 'All'
                  : f === 'pending'
                    ? `Pending (${pendingCount})`
                    : `Approved (${approvedCount})`}
              </button>
            ))}
            <div className="flex-1" />
            {/* Sort toggle */}
            <button
              onClick={() =>
                setSortOrder((prev) =>
                  prev === 'newest' ? 'oldest' : 'newest'
                )
              }
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-neutral-500 hover:text-neutral-300 hover:bg-white/[0.04] transition-colors"
              title={`Sort by ${sortOrder === 'newest' ? 'oldest' : 'newest'} first`}
            >
              <ArrowUpDown className="w-3 h-3" />
              {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
            </button>
          </div>
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-500 text-sm">Loading users...</div>
            </div>
          ) : filteredAndSortedUsers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-500 text-sm">No users found</div>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {filteredAndSortedUsers.map((user) => {
                const displayName = user.displayName || user.username
                const initials = getInitials(displayName, user.username)
                const avatarColor = getAvatarColor(user.username)
                const isMutating =
                  (approveMutation.isPending &&
                    approveMutation.variables === user.id) ||
                  (revokeMutation.isPending &&
                    revokeMutation.variables === user.id)

                return (
                  <div
                    key={user.id}
                    className="px-5 py-3.5 flex items-center gap-3 hover:bg-white/[0.02] transition-colors"
                  >
                    {/* Avatar */}
                    <div
                      className={cn(
                        'w-10 h-10 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0',
                        avatarColor
                      )}
                    >
                      <span className="text-black text-xs font-bold">
                        {initials}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium truncate">
                          {displayName}
                        </span>
                        {user.isAdmin && (
                          <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 rounded text-[10px] font-semibold">
                            <Shield className="w-2.5 h-2.5" />
                            Admin
                          </span>
                        )}
                        {user.smsSubscribed && (
                          <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-cyan-500/15 text-cyan-400 rounded text-[10px] font-semibold">
                            <MessageSquare className="w-2.5 h-2.5" />
                            SMS
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-neutral-500 text-xs truncate">
                          @{user.username}
                        </span>
                        {user.email && (
                          <>
                            <span className="text-neutral-700 text-xs">·</span>
                            <span className="text-neutral-500 text-xs truncate">
                              {user.email}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Calendar className="w-3 h-3 text-neutral-600" />
                        <span
                          className="text-neutral-600 text-[11px]"
                          title={formatDate(user.createdAt)}
                        >
                          Joined {timeAgo(user.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Status + Action */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {user.isAdmin ? (
                        <span className="px-2.5 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-xs font-medium">
                          Approved
                        </span>
                      ) : user.isApproved ? (
                        <button
                          onClick={() => revokeMutation.mutate(user.id)}
                          disabled={isMutating}
                          className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 hover:bg-red-500/15 hover:text-red-400 rounded-full text-xs font-medium transition-colors disabled:opacity-50"
                          title="Click to revoke access"
                        >
                          <Check className="w-3 h-3" />
                          Approved
                        </button>
                      ) : (
                        <button
                          onClick={() => approveMutation.mutate(user.id)}
                          disabled={isMutating}
                          className="flex items-center gap-1 px-2.5 py-1 bg-amber-500/15 text-amber-400 hover:bg-emerald-500/15 hover:text-emerald-400 rounded-full text-xs font-medium transition-colors disabled:opacity-50"
                          title="Click to approve"
                        >
                          <XCircle className="w-3 h-3" />
                          Pending
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
